import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Box, Drawer, IconButton } from "@mui/material";
import Iconify from "src/components/iconify/Iconify";
import useResponsive from "../../../hooks/useResponsive";
import Logo from "../../../components/logo";
import Scrollbar from "../../../components/scrollbar";
import NavSection from "../../../components/nav-section";
import {
  appConfig,
  homeConfig,
  minigamesConfig,
  getPrizeDrawConfig,
  getStoreConfig,
  getContestConfig,
  getChallengesConfig,
  getMarketplacePacksConfig,
  getAppComponentsConfig,
} from "./mainConfig";

import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setSelectedTenant } from "src/store/slices/tenantSlice";
import TenantPopover from "src/components/tenant/TenantPopover";

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

export default function Nav({ openNav, onCloseNav }) {
  const NAV_WIDTH = 220;
  const COLLAPSE_WIDTH = 80;
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isDesktop = useResponsive("up", "lg");
  const tenants = useSelector((state) => state.tenant.tenants);
  const selectedTenant = useSelector((state) => state.tenant.selectedTenant);
  const dispatch = useDispatch();
  // const isSmallScreen = useResponsive("down", 500);

  const [collapse, setCollapse] = useState(false);

  // Initialize tenant selection from URL or Redux state on mount/refresh
  useEffect(() => {
    if (tenants.length > 0) {
      // Check if URL has a tenantId that differs from current selection
      const urlTenantId = searchParams.get("tenantId");

      if (urlTenantId) {
        const tenantFromUrl = tenants.find(
          (t) => t.tenantId === parseInt(urlTenantId, 10)
        );
        // If URL tenant exists and differs from selected, sync it
        if (tenantFromUrl && (!selectedTenant || selectedTenant.tenantId !== tenantFromUrl.tenantId)) {
          dispatch(setSelectedTenant(tenantFromUrl));
          return;
        }
      }

      // If no selected tenant, default to first tenant (but don't update URL)
      if (!selectedTenant) {
        dispatch(setSelectedTenant(tenants[0]));
      }
    }
  }, [tenants, searchParams, selectedTenant, dispatch]);

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        "& .simplebar-content": {
          height: 1,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          p: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
        }}
      >
        <Box
          sx={{
            opacity: collapse ? 0 : 1,
            transition: collapse
              ? "opacity 0.15s ease-in-out"
              : "opacity 0.2s ease-in-out 0.3s",
            visibility: collapse ? "hidden" : "visible",
            flex: collapse ? 0 : 1,
            minWidth: collapse ? 0 : "auto",
            overflow: "hidden",
          }}
        >
          <Logo />
        </Box>
        <IconButton
          onClick={() => setCollapse((prev) => !prev)}
          sx={{
            position: collapse ? "absolute" : "static",
            left: collapse ? "50%" : "auto",
            transform: collapse ? "translateX(-50%)" : "none",
          }}
        >
          <Iconify
            icon={
              collapse
                ? "eva:arrow-ios-forward-fill"
                : "eva:arrow-ios-back-fill"
            }
          />
        </IconButton>
      </Box>
      <NavSection data={homeConfig} collapse={collapse} />
      <NavSection data={appConfig} collapse={collapse} title="App" />
      <NavSection data={getPrizeDrawConfig()} collapse={collapse} title="Prize Draws" />
      <NavSection data={minigamesConfig} collapse={collapse} title="Games" />
      <NavSection data={getStoreConfig()} collapse={collapse} title="Coins" />
      <TenantPopover
        selectedTenant={selectedTenant}
        tenants={tenants}
        dispatch={dispatch}
        setSelectedTenant={setSelectedTenant}
        collapse={collapse}
      />

      <NavSection
        data={getContestConfig(selectedTenant?.tenantId).map((item) => ({
          ...item,
          title: `${selectedTenant?.shortCode ? selectedTenant.shortCode + " " : ""}${item.title}`,
        }))}
        collapse={collapse}
        title="Contest"
      />
      <NavSection
        data={getMarketplacePacksConfig(selectedTenant?.tenantId).map((item) => ({
          ...item,
          title: `${selectedTenant?.shortCode ? selectedTenant.shortCode + " " : ""}${item.title}`,
        }))}
        collapse={collapse}
        title="Packs"
      />
      <NavSection
        data={getAppComponentsConfig(selectedTenant?.tenantId).map((item) => ({
          ...item,
          title: `${selectedTenant?.shortCode ? selectedTenant.shortCode + " " : ""}${item.title}`,
        }))}
        collapse={collapse}
        title="Settings"
      />
      <NavSection
        data={getChallengesConfig(selectedTenant?.tenantId).map((item) => ({
          ...item,
          title: `${selectedTenant?.shortCode ? selectedTenant.shortCode + " " : ""}${item.title}`,
        }))}
        collapse={collapse}
        title="Challenges"
      />
      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: collapse ? COLLAPSE_WIDTH : NAV_WIDTH },
        transition: (theme) =>
          theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
      }}
    >
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              width: collapse ? COLLAPSE_WIDTH : NAV_WIDTH,
              bgcolor: "background.default",
              borderRightStyle: "dashed",
              overflowX: "hidden",
              transition: (theme) =>
                theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.standard,
                }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
